import { DatePipe, LowerCasePipe } from '@angular/common';
import { Component, computed, effect } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ShowcaseComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/showcase/showcase.component';
import { IKpiCard } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { LineGraphComponent } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.component';
import { ILineGraphInputData } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { IShowcaseAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { EmbeddedVideoPlayerComponent } from '@shared/components/embedded-video-player/embedded-video-player.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { StreamDetailsDeliverableBaseDirective } from '../../stream-details-deliverable-base.directive';
import { IShowcaseStreamAnalyticsTableData } from './showcase-stream-analytics.interface';

@Component({
  imports: [
    DatePipe,
    TranslateModule,
    LineGraphComponent,
    AnalyticsDetailsTableComponent,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    EmbeddedVideoPlayerComponent,
    StatusBadgeComponent,
    ShowcaseComponent,
    DotsSpinnerComponent,
    IconComponent,
    LowerCasePipe,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-showcase-stream-analytics',
  templateUrl: './showcase-stream-analytics.component.html',
})
export class ShowcaseStreamAnalyticsComponent extends StreamDetailsDeliverableBaseDirective<
  IShowcaseStreamAnalyticsTableData,
  IShowcaseAnalytics
> {
  public override readonly deliverableType = EDeliverableType.showcase;
  public override readonly kpiCardData: IKpiCard | null = null;
  public override readonly displayedColumns: string[] = ['timestamp', 'videoTime', 'status', 'actions'];
  public override readonly dataSource: MatTableDataSource<IShowcaseStreamAnalyticsTableData> = new MatTableDataSource();
  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.showcases?.find((s) => s.deliverableId === deliverableId);
    }

    return null;
  });

  constructor() {
    super();

    effect(() => {
      if (this.executionAnalyticsWithFindings()) {
        this.setStatus();
        this.setStreamData();
      }
    });

    effect(() => {
      this.manualFindingStatusStore?.serverRemovedIds();

      if (this.executionAnalyticsWithFindings() && this.timelineData()) {
        this.prepareChartAndTableData();
      }
    });
  }

  protected override setCounts(): void {
    return;
  }

  protected override setKeywordList(): void {
    return;
  }

  protected override prepareChartAndTableData(): void {
    const tableData: IShowcaseStreamAnalyticsTableData[] = [];
    const dotGraphData: ILineGraphInputData[] = [];
    const currentShowcaseAnalytics = this.currentDeliverable();

    const timelineData = this.timelineData() || [];
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const findings = (currentShowcaseAnalytics?.findings || []).filter((f) => !removedIds.has(f.id));

    const { sortedDictionary } = this.graphUtilities.processTimelineData(
      timelineData,
      this.stream()?.executionTime,
      this.stream()?.end,
    );

    const streamEndTime = timelineData.at(-1)?.timestamp;

    if (findings.length > 0) {
      const [firstFinding] = findings;
      const lastFinding = findings.at(-1);
      const selectedFindings = [
        firstFinding,
        lastFinding?.timestamp !== firstFinding.timestamp
          ? lastFinding
          : streamEndTime && { timestamp: streamEndTime, videoTime: 0 },
      ].filter(Boolean) as typeof findings;

      selectedFindings.forEach((finding) => {
        const { id } = finding;
        const entryTimestamp = this.dateUtils.parseDateTimeFromApi(finding.timestamp!).getTime();
        const interpolatedViewCount = this.graphUtilities.getInterpolatedViewCount(sortedDictionary, entryTimestamp);

        dotGraphData.push({
          id,
          status: currentShowcaseAnalytics?.status!,
          xAxisValue: entryTimestamp,
          yAxisValue: Math.round(interpolatedViewCount),
        });

        tableData.push({
          findingParentId: finding.parentId,
          id,
          status: currentShowcaseAnalytics?.status!,
          timestamp: finding.timestamp!,
          videoTimeLabel: AnalyticsDetailsUtilitiesService.formatVideoTime(finding.videoTime),
          videoTimeSeconds: finding.videoTime ?? 0,
        });
      });
    }

    this.dataSource.data = tableData;
    this.dataCount.set(tableData.length);
    this.dotGraphData.set([...dotGraphData].sort((a, b) => a.xAxisValue - b.xAxisValue));
  }
}
